import { defineNuxtPlugin, useRouter } from 'nuxt/app';
import { createGamSpaRefreshCoordinator, setAwGamAdLifecycleListener } from '~~/common/utils/awGamSpaRefresh';
// Flag-gated GAM debug logging (window.__gamDebug / ?gamDebug=1). See gamDebugLog.ts.
import { gamDebugLog } from '~~/common/utils/gamDebugLog';

/**
 * GAM (Google Ad Manager) SPA support.
 *
 * The AdsInteractive wrapper library exposes `window.__adsConfig.spaHardReset()`,
 * which refreshes the already registered ad slots (`googletag.pubads().refresh()`).
 * On a classic page load the library defines and renders its slots itself, but
 * on client-side SPA navigation no full reload happens, so the ads would stay
 * frozen on the first page's creatives. Calling `spaHardReset()` tells GAM to
 * re-request fresh ads for the new page.
 *
 * The direct pattern (inline defineSlot in the component headCode, no wrapper)
 * has no `spaHardReset`. Its slots are still registered on the window (and the
 * head guard prevents a duplicate defineSlot on B→A), so we fall back to calling
 * `googletag.pubads().refresh()` directly — the same effect as spaHardReset.
 *
 * WHEN we refresh — the timing is the whole point. With `out-in` page
 * transitions the new page mounts while the old page animates out, so the
 * leaving page's slot divs linger in the DOM well after `router.afterEach`. The
 * wrapper's spaHardReset resolves each slot by `document.getElementById` and, for
 * a slot id reused across both pages, would latch onto the OLD leaving div and
 * never re-target the new one (the shared ad stays blank). So we do NOT refresh
 * on a fixed delay after navigation. Instead we wait for the leaving page's ad
 * components to UNMOUNT (their divs are then gone), debounced to once per
 * navigation. Ad components don't unmount on scroll (`aw-if-visible :disabled`),
 * so an unmount reliably means a page change.
 *
 * Marketing consent is NOT checked here: the agency loader script handles
 * consent itself, so without opt-in the GAM hooks are simply absent and the
 * availability guards below short-circuit the refresh on their own.
 */

// Debounce window collapsing the leaving page's unmount burst into one refresh.
const GAM_UNMOUNT_DEBOUNCE_MS = 100;

export default defineNuxtPlugin(() => {
  const router = useRouter();

  let navigationCount = 0;

  const coordinator = createGamSpaRefreshCoordinator({
    debounceMs: GAM_UNMOUNT_DEBOUNCE_MS,
    refresh () {
      gamDebugLog('unmount-settled-refresh', 'plugin', { navigationCount });
      refreshAds();
    },
  });

  router.afterEach((to, from) => {
    navigationCount += 1;
    gamDebugLog('nav', 'plugin', { navigationCount, from: from?.fullPath, to: to?.fullPath });
    // Skip the first navigation (initial/SSR hydration). The library renders
    // its slots on load, so a refresh is only needed on subsequent SPA changes.
    if (navigationCount <= 1) {
      gamDebugLog('nav-skip-first', 'plugin', { navigationCount });
      return;
    }
    coordinator.onNavigation();
  });

  // Ad components report their mount/unmount; the coordinator uses the live
  // count to fire the refresh once the leaving page's ads are gone (see its doc).
  setAwGamAdLifecycleListener({
    onAdMount: coordinator.onAdMount,
    onAdUnmount: coordinator.onAdUnmount,
  });

  function refreshAds () {
    gamDebugLog('refresh-availability', 'plugin', { spaHardReset: typeof window.__adsConfig?.spaHardReset, googletag: typeof window.googletag });
    if (typeof window.__adsConfig?.spaHardReset === 'function') {
      // Wrapper pattern (AdsInteractive): the library exposes spaHardReset,
      // which refreshes its registered slots.
      gamDebugLog('refresh-spahardreset', 'plugin');
      window.__adsConfig.spaHardReset();
    } else if (typeof window.googletag?.pubads === 'function') {
      // Direct pattern: no wrapper, so no spaHardReset. The slots are already
      // registered on the window (the head guard prevented a duplicate
      // defineSlot), so we refresh them ourselves to re-request fresh ads for
      // the new page — the equivalent of what spaHardReset does for the wrapper.
      gamDebugLog('refresh-pubads', 'plugin');
      window.googletag.cmd.push(() => {
        window.googletag?.pubads?.().refresh();
      });
    } else {
      gamDebugLog('refresh-skip', 'plugin', { spaHardReset: typeof window.__adsConfig?.spaHardReset, googletag: typeof window.googletag });
    }
  }
});
